import { RequestResult } from './request-result';
import { LoaderService } from './../loader.service';
import { TriggerbeeRequester } from './triggerbee-requester.service';
import { LoggerFactory } from './../logger.service';
import { TriggerbeeUrlBuilder } from './triggerbee-url-builder.service';
import { Injectable } from '@angular/core';
import { TriggerbeeApi } from './../triggerbee/triggerbee-api.enum';
import { ParameterType } from './../triggerbee/triggerbee-requester.service';
import { AsyncHelper } from './../../../framework/async-helper';
import { HttpClient } from '@angular/common/http';

class MockedRoute {
  public path: string;
  public statusCode = 200;
  public data: any;
  public delayInMs: number;
}

@Injectable()
export class TriggerbeeRequesterMock extends TriggerbeeRequester {
  private errorStatusCode = 400;
  constructor(http: HttpClient, loggerFactory: LoggerFactory, urlBuilder: TriggerbeeUrlBuilder, loader: LoaderService) {
    super(http, loggerFactory, urlBuilder, loader);
  }

  public postAsync<T>(
    api: TriggerbeeApi,
    resource = '',
    parameters: any = null,
    format: ParameterType = ParameterType.Json,
  ): Promise<RequestResult<T>> {
    return this.getAsync<T>(api, resource, parameters);
  }

  public deleteAsync<T>(
    api: TriggerbeeApi,
    resource = '',
    parameters: any = null,
    format: ParameterType = ParameterType.Json,
  ): Promise<RequestResult<T>> {
    return this.getAsync<T>(api, resource, parameters);
  }

  public putAsync<T>(
    api: TriggerbeeApi,
    resource = '',
    parameters: any = null,
    format: ParameterType = ParameterType.Json,
  ): Promise<RequestResult<T>> {
    return this.getAsync<T>(api, resource, parameters);
  }

  public async getAsync<T>(
    api: TriggerbeeApi,
    resource = '',
    parameters: any = undefined,
    format: ParameterType = ParameterType.Json,
  ): Promise<RequestResult<T>> {
    const jsonPath = this.getJsonPath(api);
    this.logger.debug(`Getting "${resource}" from ${TriggerbeeApi[api]}.
                           Mocked path : ${jsonPath}.
                           Parameters : ${JSON.stringify(parameters)}`);
    if (!jsonPath) {
      this.logger.error('Unknown json path.');
    }
    if (!jsonPath.endsWith('.json')) {
      this.logger.error('Invalid json path');
    }
    this.loader.setBusy();
    const response = await this.http.get<string>(jsonPath).toPromise();
    this.loader.removeBusy();
    const data = response;
    if (!data) {
      const error = `No json mock file for the API ${TriggerbeeApi[api]}`;
      this.logger.error(error);
      return new RequestResult(error, undefined, this.errorStatusCode);
    }
    const result = this.parseAsync<T>(data, resource, api);
    return result;
  }

  public async parseAsync<T>(mockJson: string, path: string, api: TriggerbeeApi): Promise<RequestResult<T>> {
    let routes: MockedRoute[];
    // Parse json
    try {
      routes = JSON.parse(mockJson);
    } catch (e) {
      const error = 'Cannot parse the mocked json. Json was :\n' + mockJson;
      this.logger.error(error);
      return new RequestResult<T>(error, undefined, this.errorStatusCode);
    }
    // Find path
    const route = routes.find((r) => r.path === path);
    if (!route) {
      const error = `Cannot find the route with path ${path} in ${TriggerbeeApi[api]} API.`;
      this.logger.error(error);
      return new RequestResult<T>(error, undefined, this.errorStatusCode);
    }
    // Delay
    if (route.delayInMs > 0) {
      await AsyncHelper.delay(route.delayInMs);
    }
    return new RequestResult<T>(undefined, route.data as T, route.statusCode);
  }

  private getJsonPath(api: TriggerbeeApi): string {
    let jsonPath = this.urlBuilder.buildUrl(api);
    if (!jsonPath) {
      return '';
    }
    if (jsonPath[jsonPath.length - 1] === '/') {
      jsonPath = jsonPath.substring(0, jsonPath.length - 1);
    }
    return jsonPath;
  }
}
